import { useEffect, useRef, useState } from "react";
import { FloatingPortal } from "@floating-ui/react";
import { ChevronUpIcon } from "@heroicons/react/20/solid";
import { CheckIcon } from "@heroicons/react/20/solid";
import { usePopover } from "../hooks/usePopover";
import {
  formatFrames,
  formatStandard,
  formatTimecode,
  parseTimecodeToSeconds,
  type TimeFormat,
} from "../lib/timecode";
import { classNames, isNumber } from "../utils";

export interface TimeDisplayProps {
  /** Current playback time in seconds. */
  time: number;
  /** Total duration in seconds, or null before loadedmetadata fires. */
  duration: number | null;
  /** Frames per second — used for timecode formatting and parsing. */
  fps: number;
  /** When true, the standard display hides the trailing frames component (m:ss instead of m:ss:ff). */
  isCompactVideoControls: boolean;
  /** Currently active display format. */
  timeFormat: TimeFormat;
  /** Notify parent of a format change (orchestrator persists to settings). */
  setTimeFormat: (f: TimeFormat) => void;
  /** Seek the player. Called when user submits an edited timecode value. */
  onSeek: (time: number) => void;
}

/**
 * Three-mode time display + a `<select>` for switching modes.
 *
 *   standard   "1:23 / 4:56" or "01:23:04 / 04:56:18" — toggle to show remaining
 *   frames     "2780 / 8856" — frame counts (time × fps)
 *   timecode   "00:01:23:04" — editable input, supports h:m:s:f parsing
 *
 * The timecode mode bypasses React for high-frequency updates by writing
 * directly to the input ref (otherwise the whole component would re-render
 * on every animation-frame tick — measurable hiccup in long sessions).
 */
export function TimeDisplay({
  time,
  duration,
  fps,
  isCompactVideoControls,
  timeFormat,
  setTimeFormat,
  onSeek,
}: TimeDisplayProps) {
  const [showRemainingTime, setShowRemainingTime] = useState(false);
  const [isEditingTimecode, setIsEditingTimecode] = useState(false);
  const timecodeInputRef = useRef<HTMLInputElement>(null);

  // Mirror `time` into the timecode input value without forcing a React
  // re-render every animation frame. We let React render the input once with
  // a defaultValue, then keep its .value in sync via direct DOM writes.
  // Skipped while the user is actively editing so their cursor doesn't jump.
  useEffect(() => {
    if (timeFormat !== "timecode") return;
    if (isEditingTimecode) return;
    if (!timecodeInputRef.current) return;
    timecodeInputRef.current.value = formatTimecode(time, fps);
  }, [time, fps, timeFormat, isEditingTimecode]);

  const commitTimecode = (text: string) => {
    const secs = parseTimecodeToSeconds(text, fps);
    if (secs !== null && duration != null) {
      onSeek(Math.min(secs, duration));
    }
  };

  return (
    <div className="flex items-center gap-2">
      {timeFormat === "timecode" ? (
        <input
          ref={timecodeInputRef}
          defaultValue={formatTimecode(time, fps)}
          size={11}
          className="select-none bg-transparent text-sm tabular-nums text-[#282A30] outline-none focus:select-text focus:rounded focus:bg-[#F7F8F9] focus:px-1"
          onFocus={() => setIsEditingTimecode(true)}
          onBlur={(e) => {
            setIsEditingTimecode(false);
            commitTimecode(e.currentTarget.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commitTimecode(e.currentTarget.value);
              e.currentTarget.blur();
            } else if (e.key === "Escape") {
              e.currentTarget.blur();
            }
          }}
        />
      ) : timeFormat === "frames" ? (
        <div className="select-none text-sm tabular-nums text-[#282A30]">
          <span>{formatFrames(time, fps)}</span>{" "}
          /{" "}
          <span>{formatFrames(duration ?? 0, fps)}</span>
        </div>
      ) : (
        <div
          className="cursor-pointer select-none text-center text-sm tabular-nums text-[#282A30]"
          onClick={() => setShowRemainingTime((prev) => !prev)}
          title={
            showRemainingTime
              ? "Click to show elapsed time"
              : "Click to show remaining time"
          }
        >
          <span>
            {isNumber(duration) && showRemainingTime
              ? `-${formatStandard(Math.max(0, duration - time), fps, isCompactVideoControls)}`
              : formatStandard(time, fps, isCompactVideoControls)}
          </span>
          {isNumber(duration) && (
            <>
              {" "}
              /{" "}
              {formatStandard(duration, fps, isCompactVideoControls)}
            </>
          )}
        </div>
      )}

      <FormatChevronMenu value={timeFormat} onChange={setTimeFormat} />
    </div>
  );
}

const FORMAT_OPTIONS: ReadonlyArray<{ value: TimeFormat; label: string }> = [
  { value: "standard", label: "Standard" },
  { value: "timecode", label: "Timecode" },
  { value: "frames", label: "Frames" },
];

interface FormatChevronMenuProps {
  value: TimeFormat;
  onChange: (next: TimeFormat) => void;
}

/**
 * Small chevron-up button that opens a popover with the three time-format
 * options. Replaces the always-visible `<select>` — saves horizontal space
 * in the controls bar and matches the Figma's tighter layout.
 */
function FormatChevronMenu({ value, onChange }: FormatChevronMenuProps) {
  const {
    isOpen,
    setIsOpen,
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
  } = usePopover({ placement: "top" });

  return (
    <>
      <button
        type="button"
        ref={refs.setReference}
        {...getReferenceProps()}
        className={classNames(
          "flex h-5 w-5 items-center justify-center rounded text-[#71717A] transition-colors hover:text-[#282A30]",
          isOpen && "text-[#282A30]",
        )}
        title="Change time format"
        aria-label="Change time format"
      >
        <ChevronUpIcon className="h-4 w-4" />
      </button>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50 w-32 rounded-lg border border-[#DFE1E4] bg-white p-1 text-[13px] text-[#282A30] shadow-[0_8px_28px_-4px_rgba(0,0,0,0.18),_0_0_0_0.5px_rgba(0,0,0,0.06)]"
          >
            {FORMAT_OPTIONS.map((opt) => {
              const selected = value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={classNames(
                    "flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-[#F7F8F9]",
                    selected && "bg-[#F7F8F9]",
                  )}
                  role="menuitemradio"
                  aria-checked={selected}
                >
                  <span>{opt.label}</span>
                  {selected && (
                    <CheckIcon className="h-3.5 w-3.5 text-[#6526DB]" />
                  )}
                </button>
              );
            })}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
